import { InstallSectionCard } from "../InstallSectionCard";
import { useCompanyPageStore } from "../../../../../../store/companyPageStore";
import { ProductInput } from "../../../../../../components/input/ProductInput/ProductInput";
import { ProductOption } from "../../../../../../components/input/ProductInput/types";
import { EmptyState } from "../../../../components/EmptyState";

import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Skeleton from "@mui/material/Skeleton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MuiSearchIcon from "@mui/icons-material/Search";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useIntensityOverTime } from "../hooks/useIntensityOverTime";
import { IntensityTrend } from "../../../../../../legacy/queries/Intensity";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4plugins_regression from "@amcharts/amcharts4/plugins/regression";

interface ChartDataPoint {
  date: Date;
  [key: string]: number | Date;
}

const buildChartData = (
  data: IntensityTrend[],
  options: ProductOption[]
): ChartDataPoint[] => {
  const chartDataMap = new Map<string, ChartDataPoint>();

  options.forEach((product) => {
    const filtered = data.filter(
      (trend) => trend.product?.name === product.label
    );

    const normalizedData = filtered.map((trend) => ({
      productId: product.value,
      name: product.label,
      month: (trend.trend?.month ?? 1) - 1, // JS Date is 0-indexed
      year: trend.trend?.year ?? new Date().getFullYear(),
      day: 1,
      score: trend.trend?.score ?? 0,
    }));

    normalizedData.forEach((normalized) => {
      const date = new Date(normalized.year, normalized.month, normalized.day);
      const [dateKey] = date.toISOString().split("T"); // Use date as key for merging

      if (!chartDataMap.has(dateKey)) {
        chartDataMap.set(dateKey, { date });
      }

      const dataPoint = chartDataMap.get(dateKey)!;
      dataPoint[normalized.productId] = normalized.score;
    });
  });

  const chartData = Array.from(chartDataMap.values()).sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );

  // Remove most recent data point if it's less than 3 months old
  if (chartData.length > 0) {
    const mostRecentDate = chartData[chartData.length - 1]?.date;
    if (mostRecentDate) {
      const timeFromMostRecentDate = Date.now() - mostRecentDate.getTime();
      const monthsSinceMostRecentDate = Math.floor(
        timeFromMostRecentDate / (1000 * 60 * 60 * 24 * 30)
      );

      if (monthsSinceMostRecentDate < 3) {
        chartData.pop();
      }
    }
  }

  return chartData;
};

export const IntensityOverTime = () => {
  const company = useCompanyPageStore((state) => state.company);
  const [selectedProductOptions, setSelectedProductOptions] = useState<
    ProductOption[]
  >([]);
  const [yearsPeriod, setYearsPeriod] = useState<number>(1);
  const [showTrendLines, setShowTrendLines] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<am4charts.XYChart | null>(null);

  const handleProductsSelect = useCallback((products: ProductOption[]) => {
    setSelectedProductOptions(products);
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleYearSelect = (years: number) => {
    setYearsPeriod(years);
    handleMenuClose();
  };

  const getYearLabel = (years: number) => {
    switch (years) {
      case 1:
        return "One";
      case 3:
        return "Three";
      case 5:
        return "Five";
      default:
        return "One";
    }
  };

  const selectedProductIds = useMemo(
    () => selectedProductOptions.map((option) => Number(option.value)),
    [selectedProductOptions]
  );

  const { intensityOverTimeData, isLoading } = useIntensityOverTime({
    hgId: company!.hgId,
    productIds: selectedProductIds,
    lookBackYears: yearsPeriod,
  });

  const chartData = useMemo(
    () => buildChartData(intensityOverTimeData, selectedProductOptions),
    [intensityOverTimeData, selectedProductOptions]
  );

  const generateChart = useCallback(
    (chartData: ChartDataPoint[], selectedOptions: ProductOption[]) => {
      if (!chartRef.current || selectedOptions.length === 0) return;

      // Dispose existing chart
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }

      const chart = am4core.create(chartRef.current, am4charts.XYChart);
      chartInstance.current = chart;

      chart.numberFormatter.bigNumberPrefixes = [
        { number: 1e3, suffix: "k" },
        { number: 1e6, suffix: "m" },
        { number: 1e9, suffix: "bn" },
        { number: 1e12, suffix: "tn" },
      ];

      chart.numberFormatter.numberFormat = "#.#a";
      chart.zoomOutButton.disabled = true;

      // Define contrasting colors
      chart.colors.list = [
        am4core.color("#ffb74d"), // orange
        am4core.color("#81d4fa"), // light blue
        am4core.color("#66bb6a"), // green
        am4core.color("#f06292"), // pink
        am4core.color("#b2dfdb"), // light teal
        am4core.color("#fdd835"), // yellow
        am4core.color("#d4e157"), // light green
      ];

      // Create axes
      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.renderer.grid.template.location = 0;

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.grid.template.opacity = 0;

      const label = valueAxis.renderer.labels.template;
      label.truncate = true;
      label.maxWidth = 240;
      label.tooltipText = "{category}";

      // Set data
      chart.data = chartData;

      // Create series for each product
      selectedOptions.forEach((product) => {
        const lineColor = chart.colors.next().toString();

        // Main line series
        const series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = product.value;
        series.dataFields.dateX = "date";
        series.name = product.label;
        series.tooltipText = "{dateX}: [b]{valueY}[/]";
        series.strokeWidth = 3;
        series.stroke = am4core.color(lineColor);
        series.fillOpacity = 0;
        series.tensionX = 0.8; // Makes line smoother/curved

        // Hover effects
        series.defaultState.transitionDuration = 1000;
        const hoverState = series.states.create("hover");
        hoverState.properties.strokeWidth = 4;

        // Bullet styling
        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.radius = 4;
        bullet.circle.fill = am4core.color(lineColor);
        bullet.circle.stroke = am4core.color("#ffffff");
        bullet.circle.strokeWidth = 2;
        bullet.circle.fillOpacity = 1;

        // Bullet hover effect
        const bulletHoverState = bullet.states.create("hover");
        bulletHoverState.properties.scale = 1.3;

        // Custom tooltip
        bullet.tooltip = new am4core.Tooltip();
        bullet.tooltipText =
          "[#000 font-size: 12px]{valueY} Weighted Intensity found for {name} on {dateX}";
        bullet.tooltip.getFillFromObject = false;
        bullet.tooltip.background.fill = am4core.color(lineColor);
        bullet.tooltip.background.cornerRadius = 6;
        bullet.tooltip.background.fillOpacity = 0.9;
        bullet.tooltip.label.maxWidth = 150;
        bullet.tooltip.label.wrap = true;

        // Add trend line if enabled
        if (showTrendLines) {
          const trendSeries = chart.series.push(new am4charts.LineSeries());
          trendSeries.dataFields.valueY = product.value;
          trendSeries.dataFields.dateX = "date";
          trendSeries.strokeWidth = 2;
          trendSeries.strokeDasharray = "5,3";
          trendSeries.strokeOpacity = 0.6;
          trendSeries.name = `Trend for ${product.label}`;
          trendSeries.hiddenInLegend = true;
          trendSeries.stroke = am4core.color(lineColor);
          trendSeries.fillOpacity = 0;

          // Trend line styling
          trendSeries.defaultState.transitionDuration = 1000;

          const regression = trendSeries.plugins.push(
            new am4plugins_regression.Regression()
          );
          regression.simplify = true;
        }
      });

      // Add legend
      chart.legend = new am4charts.Legend();
      chart.legend.position = "bottom";
      chart.legend.itemContainers.template.clickable = false;
      chart.legend.itemContainers.template.focusable = false;
      chart.legend.itemContainers.template.cursorOverStyle =
        am4core.MouseCursorStyle.default;
      chart.legend.scrollable = true;

      if (chart.svgContainer) {
        chart.svgContainer.htmlElement.style.height = "5in";
      }
    },
    [showTrendLines]
  );

  useEffect(() => {
    if (chartData.length > 0 && selectedProductOptions.length > 0) {
      generateChart(chartData, selectedProductOptions);
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, [chartData, selectedProductOptions, generateChart]);

  const hasProducts = selectedProductOptions.length > 0;
  const hasData = !isLoading && hasProducts && chartData.length > 0;
  const isEmpty = !isLoading && hasProducts && chartData.length === 0;
  const noProductsSelected = !isLoading && selectedProductOptions.length === 0;

  return (
    <InstallSectionCard
      title="Intensity Over Time"
      height={650}
      noTitleMargin
      filterKey="INTENSITY_OVER_TIME"
    >
      <Box marginTop={3}>
        <ProductInput
          onProductsSelect={handleProductsSelect}
          selectedProductIds={selectedProductIds}
          placeholder="Products"
        />

        <Stack direction="row" alignItems="center" my={2} gap={3}>
          <Stack direction="row" alignItems="center" flex={1} gap={1}>
            <Typography variant="body1">Over the last</Typography>
            <Button
              variant="text"
              size="small"
              onClick={handleMenuClick}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                paddingLeft: "6px",
                paddingRight: "2px",
                minWidth: "auto",
                lineHeight: "unset",
                "& .MuiButton-endIcon": {
                  marginLeft: "2px",
                },
              }}
            >
              {getYearLabel(yearsPeriod)}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleYearSelect(1)}>One</MenuItem>
              <MenuItem onClick={() => handleYearSelect(3)}>Three</MenuItem>
              <MenuItem onClick={() => handleYearSelect(5)}>Five</MenuItem>
            </Menu>
            <Typography variant="body1">
              {yearsPeriod === 1 ? "year" : "years"}
            </Typography>
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={showTrendLines}
                onChange={(e) => setShowTrendLines(e.target.checked)}
              />
            }
            label="Show trend lines"
          />
        </Stack>

        <Box
          ref={chartRef}
          height={530}
          width="calc(100% - 32px)"
          sx={{
            display: hasData ? "block" : "none",
          }}
        />

        {isLoading && (
          <Box sx={{ height: 500 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" />
          </Box>
        )}

        {noProductsSelected && (
          <EmptyState
            title="No product selected"
            description="To view a product's intensity overtime, first select a product"
            icon={
              <img
                src="https://static.hginsights.com/icons/empty-content-search.png"
                alt="Empty search"
                style={{
                  width: 64,
                  height: 64,
                  marginBottom: 16,
                  opacity: 0.5,
                }}
              />
            }
            height={530}
            size="medium"
          />
        )}

        {isEmpty && (
          <EmptyState
            title="No results found"
            description="Try again with different search criteria"
            icon={<MuiSearchIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />}
            height={530}
            size="medium"
          />
        )}
      </Box>
    </InstallSectionCard>
  );
};
